import React, { useState } from "react";
import { useStore } from "react-redux";
import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { LoadingButton } from "ui";

import { useGetBoundaryFeatureCountQuery } from "fond/api";
import { AsyncOperationState } from "fond/async/redux";
import { addNotification } from "fond/notifications/redux";
import { MAX_PLANNER_EXTENDED_ADDRESS, PolygonStatus } from "fond/project/polygon";
import { downloadData, retryDownload, togglePolygonLayer } from "fond/project/polygon/redux";
import { closePolygonSelect, polygonSelectUpload } from "fond/project/redux";
import { LayerId, Store } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import { BASE_DATA_LAYERS } from "../layers";

import { BOUNDARY_IMPORT_KEYS, BOUNDARY_IMPORT_LAYER_IDS, BoundaryImportKey, exceedsAddressLimit, getAddressCount } from "./boundary";
import FlyoutFooterActions from "./FlyoutFooterActions";
import { useSaveBoundary } from "./useBoundary";

const BOUNDARY_COUNT_ID = "boundary";

const AERIAL_ERRORS: Partial<Record<string, string>> = {
  [PolygonStatus.QueryTooLarge]: "There are too many streets inside the boundary to create aerial spans and poles.",
  [PolygonStatus.UnsupportedArea]: "FOND doesn't have aerial data for this boundary yet.",
};

interface BoundaryImportChecklistProps {
  /** Called once the import of the checked layers has begun. */
  onImportStarted(): void;
  /** Called once the boundary has been saved without importing any data. */
  onSkipped(): void;
}

/**
 * Checklist of FOND data to import from inside a newly drawn or uploaded boundary.
 */
const BoundaryImportChecklist: React.FC<BoundaryImportChecklistProps> = ({ onImportStarted, onSkipped }) => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();
  const { selectedArea, selectedLayers, selectedData, downloadState, status, isTooBig } = useAppSelector((state) => state.project.polygon);
  const { saveBoundary, isSaving } = useSaveBoundary();
  // Aerial is only selected in polygon select once importing, as selecting it switches downloads to aerial data only.
  const [includeAerial, setIncludeAerial] = useState(false);
  const [isDownloadingAerial, setIsDownloadingAerial] = useState(false);
  const [aerialError, setAerialError] = useState<string | null>(null);

  const { data: featureCounts, isFetching: isCounting } = useGetBoundaryFeatureCountQuery(
    selectedArea && !isTooBig ? { boundaries: [{ id: BOUNDARY_COUNT_ID, geometry: selectedArea.geometry }] } : skipToken
  );
  const addressCount = getAddressCount(featureCounts?.FeatureCounts[BOUNDARY_COUNT_ID]);
  const isDownloaded = downloadState === AsyncOperationState.success;

  const isChecked = (key: BoundaryImportKey): boolean =>
    key === "aerial" ? includeAerial : BOUNDARY_IMPORT_LAYER_IDS[key].every((layerId) => selectedLayers[layerId]);

  const setPolygonLayersSelected = (layerIds: LayerId[], selected: boolean) => {
    const { selectedLayers: currentLayers } = store.getState().project.polygon;
    layerIds.filter((layerId) => !!currentLayers[layerId] !== selected).forEach((layerId) => dispatch(togglePolygonLayer(layerId)));
  };

  const handleToggle = (key: BoundaryImportKey) => {
    if (key === "aerial") {
      setIncludeAerial((prev) => !prev);
      setAerialError(null);
    } else {
      setPolygonLayersSelected(BOUNDARY_IMPORT_LAYER_IDS[key], !isChecked(key));
    }
  };

  const getCount = (key: BoundaryImportKey): string => {
    if (key === "addresses") {
      if (!addressCount) return isCounting ? "…" : "";
      return `${addressCount.count.toLocaleString()}${addressCount.isMinimum ? "+" : ""}`;
    }
    if (key === "aerial") return "";
    if (downloadState === AsyncOperationState.executing) return "…";
    return isDownloaded ? selectedData[BOUNDARY_IMPORT_LAYER_IDS[key][0]].features.length.toLocaleString() : "";
  };

  const isOverAddressLimit = isChecked("addresses") && exceedsAddressLimit(addressCount);
  const canImport =
    BOUNDARY_IMPORT_KEYS.some(isChecked) &&
    isDownloaded &&
    status === PolygonStatus.ValidArea &&
    !isOverAddressLimit &&
    !(isChecked("addresses") && isCounting);

  const handleImport = async () => {
    if (includeAerial) {
      setIsDownloadingAerial(true);
      setPolygonLayersSelected(BOUNDARY_IMPORT_LAYER_IDS.aerial, true);
      await dispatch(downloadData(selectedArea));
      setIsDownloadingAerial(false);

      const { polygon } = store.getState().project;
      if (polygon.downloadState !== AsyncOperationState.success || polygon.status !== PolygonStatus.ValidArea) {
        setAerialError(AERIAL_ERRORS[polygon.status ?? ""] ?? "There was a problem creating aerial spans and poles. Please try again.");
        // Restore the address, parcel and street data that the aerial download replaced.
        setPolygonLayersSelected(BOUNDARY_IMPORT_LAYER_IDS.aerial, false);
        dispatch(retryDownload());
        return;
      }
    }
    dispatch(polygonSelectUpload());
    onImportStarted();
  };

  const handleSkip = async () => {
    try {
      await saveBoundary(selectedArea);
      dispatch(closePolygonSelect());
      dispatch(addNotification("Boundary saved."));
      onSkipped();
    } catch {
      dispatch(addNotification("There was a problem saving the boundary. Please try again.", { variant: "error" }));
    }
  };

  const renderWarning = () => {
    if (isTooBig) return "The boundary is too large to import FOND data from.";
    if (status === PolygonStatus.InvalidArea) return "The boundary overlaps itself. Redraw it to import FOND data.";
    if (status === PolygonStatus.QueryTooLarge) return "There are too many features inside the boundary to import.";
    if (status === PolygonStatus.UnsupportedArea) return "FOND doesn't have data for this boundary yet.";
    if (isOverAddressLimit) {
      return `The boundary contains more than ${MAX_PLANNER_EXTENDED_ADDRESS.toLocaleString()} addresses. Untick Addresses or draw a smaller boundary.`;
    }
    return aerialError;
  };
  const warning = renderWarning();

  return (
    <Box display="flex" flexDirection="column" gap={1.25}>
      <Typography variant="body2" fontWeight={500}>
        Import FOND data inside this boundary
      </Typography>
      <Box border={1} borderColor="divider" borderRadius={1} bgcolor="background.paper">
        {BOUNDARY_IMPORT_KEYS.map((key) => (
          <Box key={key} display="flex" alignItems="center" px={1.25} borderBottom={1} borderColor="divider">
            <FormControlLabel
              sx={{ flex: 1, m: 0 }}
              control={<Checkbox size="small" checked={isChecked(key)} onChange={() => handleToggle(key)} />}
              label={<Typography variant="body2">{BASE_DATA_LAYERS[key].name}</Typography>}
            />
            <Typography variant="caption" color="text.secondary">
              {getCount(key)}
            </Typography>
          </Box>
        ))}
      </Box>
      {downloadState === AsyncOperationState.failure && !status && (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => dispatch(retryDownload())}>
              Retry
            </Button>
          }
        >
          There was a problem downloading FOND data for this boundary.
        </Alert>
      )}
      {warning && <Alert severity="warning">{warning}</Alert>}
      <FlyoutFooterActions>
        <LoadingButton variant="text" size="small" onClick={handleSkip} loading={isSaving} disabled={status === PolygonStatus.InvalidArea}>
          Skip to addresses
        </LoadingButton>
        <LoadingButton variant="contained" size="small" onClick={handleImport} loading={isDownloadingAerial} disabled={!canImport}>
          Import
        </LoadingButton>
      </FlyoutFooterActions>
    </Box>
  );
};

export default BoundaryImportChecklist;
